// Blocker-code parser. Backend returns `{ code, message }`; we reduce
// the code into a discriminated union so consumers can render with
// proper i18n instead of relying on the server's English `message`.

import type {
  BlockerCode,
  BlockerDTO,
  OT,
  OTDocSlotId,
} from "@/features/transit/api/types"
import { hasExpiredBV } from "@/features/transit/lib/ccc"

export function parseBlocker(dto: BlockerDTO): BlockerCode {
  const raw = dto.code

  if (raw.startsWith("missingDoc:")) {
    return {
      kind: "missingDoc",
      slotId: raw.slice("missingDoc:".length) as OTDocSlotId,
    }
  }

  // Backend codes can include a payload after a `:` for lot-scoped
  // blockers. e.g. `lotAwaitingCCC:LOT-2026-0007`. We accept both forms.
  const [head, payload] = raw.includes(":") ? raw.split(":", 2) : [raw, undefined]

  switch (head) {
    case "freightForwarder":
    case "vessel":
    case "voyage":
    case "etd":
    case "pol":
    case "noLots":
    case "saigic":
    case "noFormuleDefinitive":
    case "noRedevance":
    case "noContainers":
    case "noTotalWeight":
    case "noPhotos":
    case "noRapportEmpotage":
    case "noTicketPesee":
    case "blMismatchUnacked":
    case "noOkToPrint":
      return { kind: head }
    case "lotAwaitingCCC":
      return { kind: "lotAwaitingCCC", lotId: payload }
    case "lotBARejected":
      return { kind: "lotBARejected", lotId: payload }
    case "lotMissingCCCDoc":
      return { kind: "lotMissingCCCDoc", lotId: payload }
    case "lotExpiredBV":
      return { kind: "lotExpiredBV", lotId: payload }
    default:
      return { kind: "unknown", raw }
  }
}

// i18n key for a parsed blocker. Used together with `t(..., { ns: "transit" })`
// — the values returned here are NEVER displayed verbatim.
export interface ResolvedBlocker {
  i18nKey: string
  values?: Record<string, string | number>
  fallback: string
}

export function resolveBlocker(
  dto: BlockerDTO,
): ResolvedBlocker {
  const parsed = parseBlocker(dto)

  switch (parsed.kind) {
    case "missingDoc":
      return {
        i18nKey: "blockers.missingDoc",
        values: { slot: parsed.slotId },
        fallback: dto.message,
      }
    case "lotAwaitingCCC":
      return {
        i18nKey: "blockers.lotAwaitingCCC",
        values: { lotId: parsed.lotId ?? "" },
        fallback: dto.message,
      }
    case "lotBARejected":
      return {
        i18nKey: "blockers.lotBARejected",
        values: { lotId: parsed.lotId ?? "" },
        fallback: dto.message,
      }
    case "lotMissingCCCDoc":
      return {
        i18nKey: "blockers.lotMissingCCCDoc",
        values: { lotId: parsed.lotId ?? "" },
        fallback: dto.message,
      }
    case "lotExpiredBV":
      return {
        i18nKey: "blockers.lotExpiredBV",
        values: { lotId: parsed.lotId ?? "" },
        fallback: dto.message,
      }
    case "unknown":
      return { i18nKey: "blockers.unknown", fallback: parsed.raw }
    default:
      return { i18nKey: `blockers.${parsed.kind}`, fallback: dto.message }
  }
}

// Synthesises blockers the FE can decide locally — used to keep the
// footer's "ready" / Advance state honest without waiting for the next
// detail refetch (and without trusting the server alone for things we
// can validate now). Currently:
//
//   - ANALYSE: every BV-decided lot must have at least one CCC doc.
//   - ANALYSE: any BV that has aged past CCC_BV_VALIDITY_DAYS expires
//     and must be reissued before the OT can advance.
//
// Merge with `ot.blockers` via `mergeBlockers` to dedupe.
export function computeClientBlockers(ot: OT | undefined | null): BlockerDTO[] {
  if (!ot) return []
  const out: BlockerDTO[] = []
  if (ot.stage === "ANALYSE") {
    for (const link of ot.lots ?? []) {
      const lotIdLabel = link.lot?.lotNumber ?? String(link.lotId)
      if (link.cccKind === "BV" && (link.cccDocs?.length ?? 0) === 0) {
        out.push({
          code: `lotMissingCCCDoc:${lotIdLabel}`,
          message: `Missing CCC document for lot ${lotIdLabel}`,
        })
      }
      if (hasExpiredBV(link)) {
        out.push({
          code: `lotExpiredBV:${lotIdLabel}`,
          message: `Lot ${lotIdLabel}'s BV is expired — issue a fresh BV`,
        })
      }
    }
  }
  return out
}

// Combines server blockers with locally-computed ones, deduped by code.
export function mergeBlockers(
  server: BlockerDTO[] | null | undefined,
  client: BlockerDTO[],
): BlockerDTO[] {
  const seen = new Set<string>()
  const out: BlockerDTO[] = []
  for (const b of [...(server ?? []), ...client]) {
    if (seen.has(b.code)) continue
    seen.add(b.code)
    out.push(b)
  }
  return out
}
